//@import dependencies
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
//@end

//@import components
import AlertComponent from 'app_components/commons/alert'
import DataTableComponent from 'app_components/commons/dataTable'
import SchedulingSkeleton from './schedulingSkeleton'
//@end

// @import hooks
// @end

// @INFO Import types
import {ISchedulingRole} from 'app_types/campus/scheduling/schedulingModuleTypes'
// @end

// @INFO Import services
// @end

// @INFO Import Styles
import './../schedulingModule.scss'
import moment from 'moment'
import TimezoneService from 'app_services/user/timezoneService'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import ModalAttached from 'app_components/modules/ModalAttached/ModalAttached'

// @ADD Component types
interface ISchedulingModuleLayoutProps {
  status: null | 'loading' | 'error'
  programData: {name: string, service_id: string, technical_guideline?: string, schedulingMode: {name: string, _id: string}} | null
  schedulingData: Array<any>
  role: ISchedulingRole
  title: string;
}

const DATE_SESSION_FORMAT = 'hh:mm a'
const YEAR_DATE_FORMAT = 'DD/MM/YYYY'

const SchedulingModuleLayout: React.FC<ISchedulingModuleLayoutProps> = (props) => {

  const { t } = useTranslation()
  const [headerKeys, setHeaderKeys] = useState<string[]>([])
  const [propsKeys, setPropsKeys] = useState<string[]>([])
  const [openTechnicalGuideline, setOpenTechnicalGuideline] = useState<string | undefined>()

  const currentTimezone = useMemo(() => {
    const timezoneService = new TimezoneService()
    return timezoneService.getCurrentTimezone()
  }, [])

  const initDataTable = () => {
    const headers = []
    let keys: string[] = []
    if (props.role === 'teacher') {
      headers.push(`${t('modules.scheduling_module.headers.client')}`)
      headers.push(`${t('modules.scheduling_module.headers.course_code')}`)
      headers.push(`${t('modules.scheduling_module.headers.course')}`)
      headers.push(`${t('modules.scheduling_module.headers.scheduling_mode')}`)
      headers.push(`${t('modules.scheduling_module.headers.city')}`)
      headers.push(`${t('modules.scheduling_module.headers.start')}`)
      headers.push(`${t('modules.scheduling_module.headers.duration')}`)
      headers.push(`${t('modules.scheduling_module.headers.schedule')}`)

      keys = ['client','course_code','course_name','scheduling_mode','city','start_date','duration', 'schedule']

    } else if (props.role === 'student') {
      headers.push(`${t('modules.scheduling_module.headers.course')}`)
      headers.push(`${t('modules.scheduling_module.headers.start')}`)
      headers.push(`${t('modules.scheduling_module.headers.duration')}`)
      headers.push(`${t('modules.scheduling_module.headers.schedule')}`)

      keys = ['course_name','start_date','duration', 'schedule']
    }
    setHeaderKeys(headers)
    setPropsKeys(keys)
  }

  useEffect(() => {
    initDataTable()
  }, [props.role])

  const getScheduleString = (startDate: string, duration: number) => {
    if (!startDate) return ' - '
    const start = moment(startDate).format(DATE_SESSION_FORMAT)
    const end = moment(startDate).add(duration, 'seconds').format(DATE_SESSION_FORMAT)
    return `${start} a ${end}`
  }

  const schedulingData = useMemo(() => {
    if (props.schedulingData?.length) {
      return props.schedulingData?.map((scheduling) => ({
        ...scheduling,
        schedule: getScheduleString(scheduling.startDate, scheduling.durationOnSeconds),
        start_date: moment(scheduling.startDate).format(YEAR_DATE_FORMAT)
      }))
    } else {
      return []
    }
  }, [props.schedulingData])

  const handleClickOpenTechnicalGuideline = useCallback(() => {
    setOpenTechnicalGuideline(props.programData?.technical_guideline)
  }, [props.programData])

  return(
    <div className='scheduling_module-component'>
      <div className='title'>
        {t(props.title)}
      </div>
      <hr />
      <div className='table-format-standar main-table'>
        {props.status === 'loading' ? (
          <SchedulingSkeleton />
        ): (
          <>
          {props.programData && (
            <div className='scheduling-module--general-info'>
              <div>
                <b>{t('modules.scheduling_module.service_id')}: </b>{props.programData.service_id} <br />
                <b>{t('modules.scheduling_module.program.name')}: </b>{props.programData.name}
              </div>
              <div>
                <b>{t('modules.scheduling_module.timezone')}: </b> {currentTimezone?.label}
              </div>
            </div>
          )}
          {!!props.programData?.technical_guideline?.length && (
            <ButtonComponent
              onClick={handleClickOpenTechnicalGuideline}
              variant='secondary'
            >
              Lineamiento técnico
            </ButtonComponent>
          )}
          {props.programData?.schedulingMode?.name === 'Virtual' ?
            <AlertComponent type='info' description='La fecha de sesión corresponde a la fecha de inicio del curso.' />
          :null}
          {schedulingData && Array.isArray(schedulingData) && schedulingData.length > 0 ? (
            <>
              <DataTableComponent
                data={schedulingData}
                headerKeys={headerKeys}
                keys={propsKeys}
                detailsKey={[]}
                onModal={null}
              />
            </>
          ): (
            <AlertComponent
              type='warning'
              title={t('modules.scheduling_module.no_data')}
            />
          )}
          {!!props.programData?.technical_guideline?.length && (
            <ModalAttached
              open={openTechnicalGuideline !== undefined}
              onClose={() => setOpenTechnicalGuideline(undefined)}
              defaultValue={openTechnicalGuideline}
              title="Lineamiento técnico"
              category='course_scheduling_technical_guideline'
              disabled
            />
          )}
          </>
        )}
      </div>
    </div>
  )
}

export default SchedulingModuleLayout
